import React from 'react';
import Heatmap from '../../widgets/charts/Heatmap';
import RegionHistogram from '../../widgets/charts/Histogram';
import DonutChart from '../../widgets/charts/DonutChart';
import PressureChart from '../../widgets/charts/PressureChart';
import Statistics from '../../widgets/Statistics/Statistics';
import VideoFeed from '../../widgets/Video/VideoFeed';

/**
 * Central registry mapping widget IDs → { component, label, defaultW, defaultH }.
 *
 * Single-foot mode uses the base IDs (pressure_heatmap, pressure_chart, …).
 * Dual-foot mode uses left/right variants (pressure_heatmap_left, …).
 * Widgets that show combined data (statistics, video_feed) appear in both modes.
 */
export const WIDGET_REGISTRY = {
  // ── Single-foot widgets ────────────────────────────────────────────
  pressure_heatmap: {
    label: 'Pressure Heatmap',
    defaultW: 5, defaultH: 6,
    minW: 3,     minH: 4,   // foot-map needs ~320px height, ~240px width
    modes: ['single'],
    render: (props) => (
      <Heatmap
        pressureData={props.pressure || {}}
        footSide={props.foot || 'left'}
      />
    ),
  },
  pressure_chart: {
    label: 'Pressure History',
    defaultW: 6, defaultH: 3,
    minW: 2,     minH: 3,
    modes: ['single'],
    render: (props) => (
      <PressureChart
        historicalData={props.historicalData || []}
        footSide={props.foot || 'left'}
      />
    ),
  },
  histogram: {
    label: 'Region Histogram',
    defaultW: 4, defaultH: 3,
    minW: 2,     minH: 3,
    modes: ['single'],
    render: (props) => (
      <RegionHistogram
        historicalData={props.historicalData || []}
        onPointe={props.onPointe || false}
      />
    ),
  },

  // ── Dual-foot left variants ────────────────────────────────────────
  pressure_heatmap_left: {
    label: 'Left Heatmap',
    defaultW: 4, defaultH: 6,
    minW: 3,     minH: 4,
    modes: ['dual'],
    render: (props) => (
      <Heatmap
        pressureData={props.leftPressure || {}}
        footSide="left"
      />
    ),
  },
  pressure_chart_left: {
    label: 'Left Pressure Chart',
    defaultW: 6, defaultH: 3,
    minW: 2,     minH: 3,
    modes: ['dual'],
    render: (props) => (
      <PressureChart
        historicalData={props.leftHistoricalData || []}
        footSide="left"
      />
    ),
  },
  donut_left: {
    label: 'Left Alignment Donut',
    defaultW: 2, defaultH: 2,
    minW: 2,     minH: 2,
    modes: ['dual'],
    render: (props) => (
      <DonutChart
        good={props.stats?.leftFootGoodCount || 0}
        ok={props.stats?.leftFootOkCount || 0}
        bad={props.stats?.leftFootBadCount || 0}
        isPlaceholder={!props.leftOnPointe}
      />
    ),
  },

  // ── Dual-foot right variants ───────────────────────────────────────
  pressure_heatmap_right: {
    label: 'Right Heatmap',
    defaultW: 4, defaultH: 6,
    minW: 3,     minH: 4,
    modes: ['dual'],
    render: (props) => (
      <Heatmap
        pressureData={props.rightPressure || {}}
        footSide="right"
      />
    ),
  },
  pressure_chart_right: {
    label: 'Right Pressure Chart',
    defaultW: 6, defaultH: 3,
    minW: 2,     minH: 3,
    modes: ['dual'],
    render: (props) => (
      <PressureChart
        historicalData={props.rightHistoricalData || []}
        footSide="right"
      />
    ),
  },
  donut_right: {
    label: 'Right Alignment Donut',
    defaultW: 2, defaultH: 2,
    minW: 2,     minH: 2,
    modes: ['dual'],
    render: (props) => (
      <DonutChart
        good={props.stats?.rightFootGoodCount || 0}
        ok={props.stats?.rightFootOkCount || 0}
        bad={props.stats?.rightFootBadCount || 0}
        isPlaceholder={!props.rightOnPointe}
      />
    ),
  },

  // ── Shared widgets (both modes) ────────────────────────────────────
  statistics: {
    label: 'Statistics Panel',
    defaultW: 4, defaultH: 4,
    minW: 2,     minH: 3,
    modes: ['single', 'dual'],
    render: (props) => (
      <Statistics statistics={props.stats || {}} />
    ),
  },
  video_feed: {
    label: 'Video Feed',
    defaultW: 5, defaultH: 6,  // larger default — many controls
    minW: 3,     minH: 5,      // 5 rows × 80px = 400px matches min-height
    modes: ['single', 'dual'],
    render: (props) => (
      <VideoFeed
        onCapture={props.onCapture}
        isRecording={props.isRecording || false}
      />
    ),
  },
};

/** Returns registry entries valid for a given mode. */
export function getWidgetsForMode(mode) {
  return Object.entries(WIDGET_REGISTRY)
    .filter(([_, def]) => def.modes.includes(mode))
    .map(([id, def]) => ({ id, ...def }));
}
